/** tm/components/govPeriod.ts — 공고 모집기간 한 줄 규칙 (2026-10-07 지도사 「모집기간도 나와야지 → 월별로 선택」)
 *
 *  GovPage 추천 문자(recoBody)와 업체 상세 추천 카드(GovRecoList)가 같은 규칙을 쓰려고 한 벌로 뺐다.
 *  로움 공고는 상시 칸이 따로 없다 — 피드(prefill)가 상시·예산 소진 공고를 deadline NULL + first_come 으로 싣고,
 *  공개 피드(public-gov)도 deadline NULL 을 상시로 본다. 그래서 마감 없음 = 상시, 마감 없고 선착순도 아니면 「공고문 확인」.
 *  매년 반복(recur_yearly) 공고는 마감이 지났어도 내년 같은 날짜로 본다 — fphub 달력의 「내년 같은 달(전년도 공고 기준)」과 같은 뜻.
 */
import type { GovProgram } from '../types';
import { fmtDate, dDay, today } from '../hooks';

export type GovPeriodInput = Pick<GovProgram, 'deadline' | 'apply_start' | 'first_come' | 'recur_yearly'>;

export interface GovPeriod {
  /** 기간 — 「2026.10.01 ~ 2026.11.30」 「상시」 「공고문 확인」 「2026.10.01 ~ 마감 공고문 확인」 */
  text: string;
  /** 꼬리말 — 「선착순」 「매년 반복(전년 공고 기준)」 (없으면 '') — 화면은 text · note 로 잇고, 좁으면 note 앞에서 줄을 바꾼다 */
  note: string;
  /** 마감까지 남은 날(마감 없으면 null · 매년 반복은 내년 날짜 기준) */
  dday: number | null;
  /** 마감이 지났다 (매년 반복 공고는 내년으로 보므로 false) */
  expired: boolean;
  /** 매년 반복 공고를 전년도 날짜 + 1년으로 본 경우 */
  recurNext: boolean;
  /** 월 겹침 판정용 구간(YYYY-MM-DD) — null = 열린 끝 */
  start: string | null;
  end: string | null;
  /** 상시(마감 없음) */
  always: boolean;
}

const d10 = (v: string | null | undefined): string | null => (v ? String(v).slice(0, 10) : null) || null;
/** YYYY-MM-DD 에 n년 더하기 — 2월 29일은 28일로 */
function addYears(d: string, n: number): string {
  const y = Number(d.slice(0, 4)) + n;
  const md = d.slice(5);
  return md === '02-29' ? `${y}-02-28` : `${y}-${md}`;
}

export function govPeriod(p: GovPeriodInput, todayStr: string = today()): GovPeriod {
  let start = d10(p.apply_start);
  let end = d10(p.deadline);
  const always = !end;
  let expired = !!end && end < todayStr;
  let recurNext = false;
  if (expired && p.recur_yearly === true && end) {
    // 전년도 공고 — 내년(또는 그 다음) 같은 날짜로. 3년 넘게 지난 공고는 그대로 지난 공고
    for (let i = 0; i < 3 && end < todayStr; i++) { end = addYears(end, 1); if (start) start = addYears(start, 1); }
    if (end >= todayStr) { expired = false; recurNext = true; }
  }
  const head = start ? `${fmtDate(start)} ~ ` : '';
  const text = end ? `${head}${fmtDate(end)}` : p.first_come ? `${head}상시` : start ? `${head}마감 공고문 확인` : '공고문 확인';
  const note = [p.first_come ? '선착순' : '', recurNext ? '매년 반복(전년 공고 기준)' : ''].filter(Boolean).join(' · ');
  return { text, note, dday: end ? dDay(end) : null, expired, recurNext, start, end, always };
}

/** 추천 문자용 신청기한 한 줄 — GovPage recoBody 가 쓰던 문구 그대로 (D-day 를 글에 넣는다) */
export function govPeriodSms(p: GovPeriodInput): string {
  const dd = p.deadline ? dDay(p.deadline) : null;
  const dl = dd !== null ? (dd >= 0 ? `D-${dd}` : `D+${Math.abs(dd)}`) : '';
  const head = p.apply_start ? `${fmtDate(p.apply_start)} ~ ` : '';
  if (p.deadline) return `${head}${fmtDate(p.deadline)} (${dl})${p.first_come ? ' · 선착순' : ''}`;
  return p.first_come ? `${head}선착순 (예산 소진 시 마감)` : '공고문 확인';
}

/** 이번 달부터 n개월 — { y, m, key:'YYYY-MM', first:'YYYY-MM-01', last:'YYYY-MM-DD' } */
export interface MonthSlot { y: number; m: number; key: string; first: string; last: string }
export function monthSlots(n = 12, todayStr: string = today()): MonthSlot[] {
  const y0 = Number(todayStr.slice(0, 4)), m0 = Number(todayStr.slice(5, 7));
  const out: MonthSlot[] = [];
  for (let i = 0; i < n; i++) {
    const idx = m0 - 1 + i, y = y0 + Math.floor(idx / 12), m = (idx % 12) + 1;
    const mm = String(m).padStart(2, '0');
    const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();   // 다음 달 0일 = 이 달 마지막 날
    out.push({ y, m, key: `${y}-${mm}`, first: `${y}-${mm}-01`, last: `${y}-${mm}-${String(lastDay).padStart(2, '0')}` });
  }
  return out;
}

/** 모집기간이 그 달과 겹치나 — 상시(마감 없음)는 신청 시작일만 본다(시작 전 달은 아님) */
export function periodInMonth(pr: GovPeriod, slot: MonthSlot): boolean {
  if (pr.start && pr.start > slot.last) return false;
  if (pr.end && pr.end < slot.first) return false;
  return true;
}
